import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Appointment } from './useData';
import { useAuth } from '../../contexts/AuthContext';
import { fetcher } from '../../services/fetcher';
import useSWR from 'swr';
import { updateAppointment } from '../../services/mutations';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrBefore);

type ViewMode = 'day' | 'week' | 'month';

interface CalendarEvent {
  id: string;
  title: string;
  clientName: string;
  appointmentName: string;
  start: Date;
  end: Date;
  color: string;
  appointment: Appointment;
}

const statusColors: Record<number, { bg: string; text: string; border: string }> = {
  1: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' }, // Opened
  2: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' }, // Closed
  3: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' }, // Canceled
  4: { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' }, // Rescheduled
};

const statusGradients: Record<number, string> = {
  1: 'from-amber-400 via-amber-500 to-amber-600',
  2: 'from-green-400 via-green-500 to-green-600',
  3: 'from-red-400 via-red-500 to-red-600',
  4: 'from-cyan-400 via-cyan-500 to-cyan-600',
};

const getColorBasedOnStatus = (status?: number): { bg: string; text: string; border: string; gradient: string } => {
  const defaultColor = { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', gradient: 'from-gray-400 via-gray-500 to-gray-600' };
  if (!status) return defaultColor;
  const colors = statusColors[status] || defaultColor;
  const gradient = statusGradients[status] || defaultColor.gradient;
  return { ...colors, gradient };
};

const formatTime = (dateTime?: string): string => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/Athens',
  });
};

const AppointmentClientContacts: React.FC<{
  email?: string;
  phone?: string;
  className?: string;
}> = ({ email, phone, className }) => {
  const normalizedEmail = email?.trim();
  const normalizedPhone = phone?.trim();

  if (!normalizedEmail && !normalizedPhone) {
    return null;
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1', className)}>
      {normalizedEmail && (
        <span className="inline-flex min-w-0 items-center gap-1">
          <svg
            className="h-3 w-3 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <span className="truncate">{normalizedEmail}</span>
        </span>
      )}
      {normalizedPhone && (
        <span className="inline-flex items-center gap-1">
          <svg
            className="h-3 w-3 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.517l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          <span>{normalizedPhone}</span>
        </span>
      )}
    </div>
  );
};

const CalendarView: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { colors } = useTheme();
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [showMyAppointments, setShowMyAppointments] = useState(true);
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<{ date: Dayjs; hour?: number } | null>(null);

  // Fetch appointments
  const url = showMyAppointments && user?.id
    ? `/crm-clients-appointments/all_of_user/${user.id}`
    : '/crm-clients-appointments';
  
  const { data: appointmentsData, error, isLoading, mutate } = useSWR<any>(
    url,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  // Transform appointments to calendar events
  const events = useMemo<CalendarEvent[]>(() => {
    if (!appointmentsData) return [];

    const appointments: Appointment[] = showMyAppointments && Array.isArray(appointmentsData)
      ? appointmentsData
      : appointmentsData?.records || [];

    return appointments
      .filter((app) => app.clientAppScheduleDateTime)
      .map((app) => {
        const start = new Date(app.clientAppScheduleDateTime!);
        const end = app.clientAppFinishDateTime
          ? new Date(app.clientAppFinishDateTime)
          : new Date(start.getTime() + (app.clientAppDurationMins || 60) * 60000);

        const colors = getColorBasedOnStatus(app.clientAppStatus);

        const clientName = app.client?.clientName || 'PRIVATE';
        const appointmentName = app.clientAppName || 'Untitled Appointment';
        
        return {
          id: app.clientAppId.toString(),
          title: `${clientName} - ${appointmentName}`,
          clientName,
          appointmentName,
          start,
          end,
          color: colors.gradient,
          appointment: app,
        };
      });
  }, [appointmentsData, showMyAppointments]);

  // Get events for current view
  const getEventsForDate = (date: Dayjs): CalendarEvent[] => {
    return events.filter((event) => {
      const eventDate = dayjs(event.start);
      return eventDate.isSame(date, 'day');
    });
  };

  const getEventsForWeek = (startDate: Dayjs): CalendarEvent[] => {
    const endDate = startDate.add(6, 'day');
    return events.filter((event) => {
      const eventDate = dayjs(event.start);
      return (eventDate.isAfter(startDate, 'day') || eventDate.isSame(startDate, 'day')) && 
             (eventDate.isBefore(endDate, 'day') || eventDate.isSame(endDate, 'day'));
    });
  };

  const getEventsForMonth = (date: Dayjs): CalendarEvent[] => {
    const startOfMonth = date.startOf('month');
    const endOfMonth = date.endOf('month');
    return events.filter((event) => {
      const eventDate = dayjs(event.start);
      return (eventDate.isAfter(startOfMonth, 'day') || eventDate.isSame(startOfMonth, 'day')) && 
             (eventDate.isBefore(endOfMonth, 'day') || eventDate.isSame(endOfMonth, 'day'));
    });
  };

  // Navigation handlers
  const handlePrevious = () => {
    if (viewMode === 'day') {
      setCurrentDate(currentDate.subtract(1, 'day'));
    } else if (viewMode === 'week') {
      setCurrentDate(currentDate.subtract(1, 'week'));
    } else {
      setCurrentDate(currentDate.subtract(1, 'month'));
    }
  };

  const handleNext = () => {
    if (viewMode === 'day') {
      setCurrentDate(currentDate.add(1, 'day'));
    } else if (viewMode === 'week') {
      setCurrentDate(currentDate.add(1, 'week'));
    } else {
      setCurrentDate(currentDate.add(1, 'month'));
    }
  };

  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  const handleEventClick = (event: CalendarEvent) => {
    navigate(`/appointments/${event.appointment.clientAppId}`);
  };

  const handleAddNew = () => {
    navigate('/appointments/new');
  };

  const handleDateDoubleClick = (date: Dayjs, hour?: number) => {
    // Format date for URL parameter
    const dateTime = hour !== undefined 
      ? date.hour(hour).minute(0).second(0).format('YYYY-MM-DDTHH:mm')
      : date.hour(9).minute(0).second(0).format('YYYY-MM-DDTHH:mm'); // Default to 9 AM
    
    navigate(`/appointments/new?clientAppScheduleDateTime=${encodeURIComponent(dateTime)}`);
  };

  const handleDragStart = (event: CalendarEvent, e: React.DragEvent) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', event.id);
    // Add visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedEvent(null);
    setDragOverSlot(null);
    // Reset visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleDragOver = (date: Dayjs, hour: number | undefined, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot({ date, hour });
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = async (date: Dayjs, hour: number | undefined, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverSlot(null);

    if (!draggedEvent) return;

    try {
      // Calculate new schedule time
      let newScheduleTime: Dayjs;
      if (hour !== undefined) {
        // For day/week views with specific hour
        newScheduleTime = date.hour(hour).minute(0).second(0).millisecond(0);
      } else {
        // For month view, keep the same time but change the date
        const originalTime = dayjs(draggedEvent.appointment.clientAppScheduleDateTime);
        newScheduleTime = date
          .hour(originalTime.hour())
          .minute(originalTime.minute())
          .second(originalTime.second())
          .millisecond(0);
      }

      // Calculate new finish time (preserve duration)
      const originalStart = dayjs(draggedEvent.appointment.clientAppScheduleDateTime);
      const originalEnd = draggedEvent.appointment.clientAppFinishDateTime
        ? dayjs(draggedEvent.appointment.clientAppFinishDateTime)
        : originalStart.add(draggedEvent.appointment.clientAppDurationMins || 60, 'minute');
      
      const duration = originalEnd.diff(originalStart, 'minute');
      const newFinishTime = newScheduleTime.add(duration, 'minute');

      // Update appointment
      await updateAppointment(draggedEvent.appointment.clientAppId, {
        clientAppScheduleDateTime: newScheduleTime.toISOString(),
        clientAppFinishDateTime: newFinishTime.toISOString(),
      });

      showNotification('Appointment moved successfully', 'success');
      mutate(); // Refresh appointments
    } catch (error: any) {
      console.error('Error moving appointment:', error);
      showNotification(error.response?.data?.message || 'Failed to move appointment', 'error');
    } finally {
      setDraggedEvent(null);
    }
  };

  // Render Day View
  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className={cn('flex flex-col h-full', colors.background)}>
        <div className={cn('border-b p-6', colors.surface, colors.border)}>
          <h2 className={cn('text-2xl font-bold', colors.text)}>{currentDate.format('dddd, MMMM D, YYYY')}</h2>
          <p className={cn('text-sm mt-1', colors.textMuted)}>{dayEvents.length} appointment{dayEvents.length !== 1 ? 's' : ''}</p>
        </div>
        <div className={cn('flex-1 overflow-y-auto p-4', colors.surface)}>
          <div className="max-w-4xl mx-auto">
            {hours.map((hour) => {
              // Get all events that overlap with this hour slot
              const hourEvents = dayEvents.filter((event) => {
                const eventStart = dayjs(event.start);
                const eventEnd = dayjs(event.end);
                const eventStartHour = eventStart.hour();
                const eventEndHour = eventEnd.hour();
                
                // Event starts in this hour
                if (eventStartHour === hour) return true;
                // Event ends in this hour
                if (eventEndHour === hour) return true;
                // Event spans across this hour
                if (eventStartHour < hour && eventEndHour > hour) return true;
                return false;
              });

              const isDragOver = dragOverSlot?.date.isSame(currentDate, 'day') && dragOverSlot?.hour === hour;
              
              return (
                <div 
                  key={hour} 
                  className={cn(
                    'border-l-2 min-h-[80px] relative mb-2 cursor-pointer transition-colors',
                    colors.border,
                    isDragOver ? colors.info : colors.surfaceHover
                  )}
                  onDoubleClick={() => handleDateDoubleClick(currentDate, hour)}
                  onDragOver={(e) => handleDragOver(currentDate, hour, e)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(currentDate, hour, e)}
                  title="Double-click to create appointment, drop to move"
                >
                  <div className={cn('absolute -left-12 top-0 w-10 text-sm font-medium', colors.textMuted)}>
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                  <div className="ml-4 p-2 relative" style={{ minHeight: '80px' }}>
                    {hourEvents.map((event) => {
                      const eventStart = dayjs(event.start);
                      const eventEnd = dayjs(event.end);
                      const eventStartHour = eventStart.hour();
                      const eventStartMinute = eventStart.minute();
                      
                      // Only show events that start in this hour slot
                      if (eventStartHour !== hour) return null;

                      const duration = eventEnd.diff(eventStart, 'minute');
                      const height = Math.max((duration / 60) * 80, 60);
                      // Calculate top position based on minutes (each hour is 80px, so each minute is 80/60 = 1.33px)
                      const topPosition = (eventStartMinute / 60) * 80;
                      const colors = getColorBasedOnStatus(event.appointment.clientAppStatus);

                      return (
                        <div
                          key={event.id}
                          draggable
                          onDragStart={(e) => handleDragStart(event, e)}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleEventClick(event)}
                          className={`group absolute left-2 right-2 rounded-xl p-3 cursor-move transition-all duration-200 hover:scale-[1.02] hover:shadow-xl border-l-4 ${colors.border} bg-gradient-to-r ${event.color} text-white shadow-lg z-10`}
                          style={{ 
                            minHeight: `${height}px`,
                            top: `${topPosition}px`,
                          }}
                        >
                          <div className="font-semibold text-sm mb-1 truncate">{event.clientName}</div>
                          <div className="text-xs font-medium mb-1 opacity-95 truncate">{event.appointmentName}</div>
                          <AppointmentClientContacts
                            email={event.appointment.client?.clientEmail}
                            phone={event.appointment.client?.clientPhone}
                            className="mb-1 text-[11px] opacity-90"
                          />
                          <div className="text-xs opacity-90 flex items-center gap-2">
                            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="truncate">
                              {formatTime(event.appointment.clientAppScheduleDateTime)} - {formatTime(event.appointment.clientAppFinishDateTime)}
                            </span>
                          </div>
                          <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 rounded-xl transition-colors"></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render Week View
  const renderWeekView = () => {
    const weekStart = currentDate.startOf('isoWeek');
    const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));
    const weekEvents = getEventsForWeek(weekStart);
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className={cn('flex flex-col h-full', colors.background)}>
        <div className={cn('border-b p-6', colors.surface, colors.border)}>
          <h2 className={cn('text-2xl font-bold', colors.text)}>
            {weekStart.format('MMM D')} - {weekStart.add(6, 'day').format('MMM D, YYYY')}
          </h2>
        </div>
        <div className={cn('flex-1 overflow-y-auto', colors.surface)}>
          <div className={cn('grid grid-cols-8 border-b-2 sticky top-0 z-10', colors.border, colors.surface)}>
            <div className={cn('border-r p-3 font-semibold text-sm', colors.border, colors.textMuted)}></div>
            {weekDays.map((day) => {
              const isToday = day.isSame(dayjs(), 'day');
              return (
                <div
                  key={day.format('YYYY-MM-DD')}
                  className={cn('border-r p-3 text-center', colors.border, isToday && colors.info)}
                >
                  <div className={cn('text-xs font-medium uppercase', colors.textMuted)}>{day.format('ddd')}</div>
                  <div className={cn('text-xl font-bold mt-1', isToday ? colors.primary : colors.text)}>
                    {day.format('D')}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-8">
            <div className={cn('border-r', colors.border, colors.surface)}>
              {hours.map((hour) => (
                <div key={hour} className={cn('border-b min-h-[80px] p-2 text-xs font-medium', colors.border, colors.textMuted)}>
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>
            {weekDays.map((day) => {
              const dayEvents = weekEvents.filter((event) =>
                dayjs(event.start).isSame(day, 'day')
              );
              const isToday = day.isSame(dayjs(), 'day');

              return (
                <div key={day.format('YYYY-MM-DD')} className={cn('border-r', colors.border, isToday ? colors.info : colors.surface)}>
                  {hours.map((hour) => {
                    const hourEvents = dayEvents.filter((event) => {
                      const eventHour = dayjs(event.start).hour();
                      const eventEndHour = dayjs(event.end).hour();
                      return eventHour <= hour && eventEndHour >= hour;
                    });

                    const isDragOver = dragOverSlot?.date.isSame(day, 'day') && dragOverSlot?.hour === hour;
                    
                    return (
                      <div 
                        key={hour} 
                        className={cn(
                          'border-b min-h-[80px] relative p-1 cursor-pointer transition-colors',
                          colors.border,
                          isDragOver ? colors.info : colors.surfaceHover
                        )}
                        onDoubleClick={() => handleDateDoubleClick(day, hour)}
                        onDragOver={(e) => handleDragOver(day, hour, e)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(day, hour, e)}
                        title="Double-click to create appointment, drop to move"
                      >
                        {hourEvents.map((event) => {
                          const eventStart = dayjs(event.start);
                          const eventEnd = dayjs(event.end);
                          const isFirstHour = eventStart.hour() === hour;

                          if (!isFirstHour) return null;

                          const duration = eventEnd.diff(eventStart, 'minute');
                          const height = Math.max((duration / 60) * 80, 50);
                          const colors = getColorBasedOnStatus(event.appointment.clientAppStatus);

                          return (
                            <div
                              key={event.id}
                              draggable
                              onDragStart={(e) => handleDragStart(event, e)}
                              onDragEnd={handleDragEnd}
                              onClick={() => handleEventClick(event)}
                              className={`absolute left-1 right-1 rounded-lg p-2 cursor-move transition-all duration-200 hover:scale-[1.02] hover:shadow-lg border-l-4 ${colors.border} bg-gradient-to-r ${event.color} text-white text-xs shadow-md z-10`}
                              style={{
                                height: `${height}px`,
                                top: '4px',
                              }}
                            >
                              <div className="font-semibold truncate text-xs">{event.clientName}</div>
                              <div className="text-xs opacity-95 truncate mt-0.5">{event.appointmentName}</div>
                              <AppointmentClientContacts
                                email={event.appointment.client?.clientEmail}
                                phone={event.appointment.client?.clientPhone}
                                className="mt-0.5 text-[10px] opacity-90"
                              />
                              <div className="text-xs opacity-90 mt-0.5">
                                {formatTime(event.appointment.clientAppScheduleDateTime)} - {formatTime(event.appointment.clientAppFinishDateTime)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render Month View
  const renderMonthView = () => {
    const monthStart = currentDate.startOf('month');
    const monthEnd = currentDate.endOf('month');
    const startDate = monthStart.startOf('isoWeek');
    const endDate = monthEnd.endOf('isoWeek');
    const days: Dayjs[] = [];
    let current = startDate;

    while (current.isSameOrBefore(endDate, 'day')) {
      days.push(current);
      current = current.add(1, 'day');
    }

    const monthEvents = getEventsForMonth(currentDate);
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
      <div className="flex-1 overflow-y-auto p-4">
        <div className={cn('rounded-2xl border overflow-hidden', colors.surface, colors.border)}>
          <div className={cn('grid grid-cols-7 border-b-2', colors.border, colors.surfaceHover)}>
            {weekDays.map((d) => (
              <div
                key={d}
                className={cn('p-2 text-center font-bold text-sm border-r last:border-r-0', colors.text, colors.border)}
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const dayEvents = monthEvents.filter((event) =>
                dayjs(event.start).isSame(day, 'day')
              );
              const isCurrentMonth = day.isSame(currentDate, 'month');
              const isToday = day.isSame(dayjs(), 'day');
              const isDragOver = dragOverSlot?.date.isSame(day, 'day') && dragOverSlot?.hour === undefined;

              return (
                <div
                  key={day.format('YYYY-MM-DD')}
                  className={cn(
                    'min-h-[120px] p-2 border-r border-b transition-colors last:border-r-0',
                    colors.border,
                    isDragOver ? colors.info : !isCurrentMonth ? 'opacity-60' : isToday ? colors.surfaceHover : ''
                  )}
                  onDoubleClick={() => handleDateDoubleClick(day)}
                  onDragOver={(e) => handleDragOver(day, undefined, e)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(day, undefined, e)}
                  title="Double-click to create appointment, drop to move"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={cn(
                        'text-sm font-semibold',
                        isToday ? cn(colors.primary, colors.primaryText, 'rounded-full w-7 h-7 flex items-center justify-center') : colors.text
                      )}
                    >
                      {day.format('D')}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className={cn('text-xs px-1.5 py-0.5 rounded-full', colors.surfaceHover, colors.textSecondary)}>
                        {dayEvents.length}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((event) => (
                      <button
                        key={event.id}
                        type="button"
                        draggable
                        onDragStart={(e) => handleDragStart(event, e)}
                        onDragEnd={handleDragEnd}
                        onClick={() => handleEventClick(event)}
                        className={cn(
                          'w-full text-left rounded px-2 py-1 text-xs truncate transition-opacity hover:opacity-90',
                          colors.info,
                          'border-l-2',
                          colors.primaryBorder
                        )}
                        title={`${event.clientName} - ${event.appointmentName} (${formatTime(event.appointment.clientAppScheduleDateTime)} - ${formatTime(event.appointment.clientAppFinishDateTime)})`}
                      >
                        <div className="truncate font-medium">
                          {event.appointmentName || event.clientName}
                        </div>
                        <AppointmentClientContacts
                          email={event.appointment.client?.clientEmail}
                          phone={event.appointment.client?.clientPhone}
                          className="mt-0.5 text-[10px]"
                        />
                      </button>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className={cn('text-xs px-2 py-0.5', colors.textMuted)}>
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center h-full', colors.background)}>
        <div className="text-center">
          <div className={cn('inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mb-4', colors.primary)}></div>
          <div className={cn('font-medium', colors.textMuted)}>Loading appointments...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('flex items-center justify-center h-full', colors.background)}>
        <div className={cn('text-center rounded-xl p-6 shadow-lg border', colors.surface, colors.border)}>
          <div className={cn('font-semibold mb-2', colors.error)}>Error loading appointments</div>
          <div className={cn('text-sm', colors.textMuted)}>{error.message}</div>
        </div>
      </div>
    );
  }

  const weekStart = currentDate.startOf('isoWeek');
  const weekEnd = weekStart.add(6, 'day');
  const dateLabel =
    viewMode === 'month'
      ? currentDate.format('MMMM YYYY')
      : viewMode === 'week'
        ? `${weekStart.format('MMM D')} – ${weekEnd.format('MMM D, YYYY')}`
        : currentDate.format('ddd, MMM D, YYYY');

  const viewTabs: { id: ViewMode; label: string }[] = [
    { id: 'month', label: 'Month' },
    { id: 'week', label: 'Week' },
    { id: 'day', label: 'Day' },
  ];

  return (
    <div className={cn('flex flex-col h-full min-h-0 flex-1', colors.background)}>
      {/* Single toolbar: icon, Month|Week|Day, prev, date, next, Today, My Appointments, New */}
      <div
        className={cn(
          'shrink-0 border-b flex flex-nowrap items-center gap-4 px-4 py-3 overflow-x-auto',
          colors.border,
          colors.surfaceHover
        )}
      >
        <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', colors.info)}>
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <div className="flex rounded-lg border overflow-hidden shrink-0" role="tablist" aria-label="Calendar view">
          {viewTabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={viewMode === tab.id}
              onClick={() => setViewMode(tab.id)}
              className={cn(
                'px-4 py-2 text-sm font-medium transition-colors',
                viewMode === tab.id ? cn(colors.primary, colors.primaryText) : cn(colors.text, colors.interactiveHover)
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handlePrevious}
            className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors', colors.border, colors.interactiveHover)}
            aria-label={viewMode === 'month' ? 'Previous month' : viewMode === 'week' ? 'Previous week' : 'Previous day'}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className={cn('min-w-[140px] sm:min-w-[180px] rounded-lg border px-3 py-2 text-center shrink-0', colors.border, colors.surface)}>
            <span className={cn('text-sm font-semibold whitespace-nowrap', colors.text)}>{dateLabel}</span>
          </div>
          <button
            onClick={handleNext}
            className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors', colors.border, colors.interactiveHover)}
            aria-label={viewMode === 'month' ? 'Next month' : viewMode === 'week' ? 'Next week' : 'Next day'}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={handleToday}
            className={cn('shrink-0 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors', colors.info, colors.interactiveHover)}
          >
            Today
          </button>
        </div>
        <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none ml-2">
          <input
            type="checkbox"
            checked={showMyAppointments}
            onChange={(e) => setShowMyAppointments(e.target.checked)}
            className="w-3.5 h-3.5 rounded focus:ring-2 focus:ring-offset-0"
          />
          <span className={cn('text-sm font-medium whitespace-nowrap', colors.text)}>My appointments only</span>
        </label>
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          <button
            onClick={() => navigate('/appointments')}
            className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors', colors.border, colors.interactiveHover)}
            title="List view"
            aria-label="View list"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>
          <button
            onClick={handleAddNew}
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg shadow-sm transition-all hover:opacity-90',
              colors.primary,
              colors.primaryText,
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black/10 dark:focus:ring-white/20'
            )}
            title="New Appointment"
            aria-label="New Appointment"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Calendar View */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'day' && renderDayView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
      </div>
    </div>
  );
};

export default CalendarView;
